import React, { ReactNode } from 'react'
import { track } from '@utils/tracking'
import { downloadImage } from '../../downloadImage'
import styles from './card.module.sass'

type Footer = {
  // "Harbour Logistics · Feb to Sep 2026 · n = 156 of 168 people"
  text: string
  // "12 excluded", "All enrolled people"; opens About this data when given onAbout.
  chip?: string
}

type Props = {
  id?: string
  question: string
  title: ReactNode
  // Opens the card's About this data panel.
  onAbout?: () => void
  // Outlined while its About this data panel is open.
  highlighted?: boolean
  // A PNG data URL of the card's chart (Chart.js toBase64Image). Only chart cards export.
  exportImage?: () => string | null
  exportName?: string
  footer?: Footer
  children: ReactNode
}

export const Card = ({ id, question, title, onAbout, highlighted = false, exportImage, exportName = 'chart', footer, children }: Props) => {
  const onExport = () => {
    const dataUrl = exportImage?.()
    if (!dataUrl) return

    downloadImage(dataUrl, exportName)
    track('FFP Dashboard: Export image', { card: exportName })
  }

  const onAboutClick = onAbout
    ? () => {
        onAbout()
        track('FFP Dashboard: About this data', { card: id ?? question })
      }
    : undefined

  const className = highlighted ? `${styles.card} ${styles.highlighted}` : styles.card

  return (
    <article id={id} className={className}>
      <header className={styles.header}>
        <div className={styles.heading}>
          <p className={styles.question}>{question}</p>
          <h3 className={styles.title}>{title}</h3>
        </div>
        <div className={styles.actions}>
          <AboutButton onAbout={onAboutClick} />
          <ExportButton exportImage={exportImage} onExport={onExport} />
        </div>
      </header>
      {children}
      <CardFooter footer={footer} onAbout={onAboutClick} />
    </article>
  )
}

const AboutButton = ({ onAbout }: { onAbout?: () => void }) => {
  if (!onAbout) return null

  return (
    <button type="button" className={styles.action} onClick={onAbout}>
      <span className={styles.infoIcon} aria-hidden="true">i</span>
      About this data
    </button>
  )
}

const ExportButton = ({ exportImage, onExport }: { exportImage?: () => string | null; onExport: () => void }) => {
  if (!exportImage) return null

  return (
    <button type="button" className={styles.action} onClick={onExport}>
      Export image
    </button>
  )
}

const CardFooter = ({ footer, onAbout }: { footer?: Footer; onAbout?: () => void }) => {
  if (!footer) return null

  const chip = footer.chip
    ? <button type="button" className={styles.chip} onClick={onAbout} disabled={!onAbout}>{footer.chip}</button>
    : null

  return (
    <footer className={styles.footer}>
      <p className={styles.footerText}>{footer.text}</p>
      {chip}
    </footer>
  )
}
